import { AnalyticsHooks } from '@frontend/analytics-shared';
import { useHasAccess as useHasBrandSettingsAccess } from '@frontend/brand-settings';
import { useBulkMessagingAcls } from '@frontend/bulk-messaging';
import { useTranslation } from '@frontend/i18n';
import { useHasInsuranceVerificationACLAccess, useHasWIVOrEIVAccess } from '@frontend/insurance-verification';
import { SettingsRoutes } from '@frontend/settings-routing';

export type AllPathsMap = Record<SettingsRoutes, { label: string; show?: boolean }>;
type NavItemsPaths = SettingsRoutes;

type NavItem = {
  label: string;
  path: NavItemsPaths;
  show?: boolean;
  hasBottomSpacing?: boolean;
  description?: string;
};

function useSettingsNavItems() {
  const { t } = useTranslation('settingsNav');
  const { hasInsuranceVerificationAccess, isDental } = useHasWIVOrEIVAccess();
  const { hasAccessToInsuranceVerification: hasACLAccess } = useHasInsuranceVerificationACLAccess();
  const { hasBulkMessagingAccess } = useBulkMessagingAcls();
  const hasAnalyticsAdminAccess = AnalyticsHooks.useHasAdminAccess();
  const { hasAccess: hasBrandSettingsAccess } = useHasBrandSettingsAccess();

  const navItems: NavItem[] = [
    {
      path: '/analytics',
      label: t('Analytics') /* AUTO-GENERATED */,
      description:
        'High-level analytics dashboard providing snapshots of calls, messages, appointments, and other KPIs.' /* AUTO-GENERATED */,
      show: hasAnalyticsAdminAccess,
    },
    {
      path: '/brand',
      label: t('Brand') /* AUTO-GENERATED */,
      show: hasBrandSettingsAccess,
    },
    {
      path: '/digital-forms',
      label: t('Forms') /* AUTO-GENERATED */,
      description:
        'Shortcut to the digital forms module. View, create, or manage practice forms in a centralized place.' /* AUTO-GENERATED */,
    },
    {
      path: '/email',
      label: t('Email') /* AUTO-GENERATED */,
      description:
        'Email marketing settings. Manage email addresses and domains used with email marketing.' /* AUTO-GENERATED */,
      show: hasBulkMessagingAccess,
    },
    {
      path: '/fax',
      label: t('Fax') /* AUTO-GENERATED */,
      description:
        'Primary fax module overview, providing quick access to inbox, drafts, and fax configuration.' /* AUTO-GENERATED */,
    },
    {
      path: '/image-gallery',
      label: t('Image Gallery') /* AUTO-GENERATED */,
      description:
        'Manage practice images and media assets used across various communications and forms.' /* AUTO-GENERATED */,
    },
    {
      path: '/insurance',
      label: t('Insurance') /* AUTO-GENERATED */,
      description: 'Manage insurance verification settings and payer connection configurations.' /* AUTO-GENERATED */,
      show: isDental && hasInsuranceVerificationAccess && hasACLAccess, // Note: Remove the isDental check when WIV for non dental locations is supported
    },
    {
      path: '/messaging',
      label: t('Messaging') /* AUTO-GENERATED */,
      description:
        'Primary messaging module overview. May contain shortcuts to inbox, archived messages, and templates.' /* AUTO-GENERATED */,
    },
    {
      path: '/payments',
      label: t('Payments') /* AUTO-GENERATED */,
      description:
        'Main payments overview page. Access invoices, statements, settings, and payment-related features.' /* AUTO-GENERATED */,
    },
    {
      path: '/phone/main',
      label: t('Phone') /* AUTO-GENERATED */,
      description: 'Configure main phone system settings including general telephony preferences.' /* AUTO-GENERATED */,
    },
    {
      path: '/reviews',
      label: t('Social') /* AUTO-GENERATED */,
      show: !hasBrandSettingsAccess, // Hide Social when Brand Settings is available
    },
    {
      path: '/schedule',
      label: t('Schedule') /* AUTO-GENERATED */,
      description: 'Primary scheduling hub, linking to calendar, appointment' /* AUTO-GENERATED */,
      hasBottomSpacing: true,
    },
    {
      path: '/subscriptions',
      label: t('Subscriptions') /* AUTO-GENERATED */,
      description:
        'View and manage your current subscriptions, billing, and feature access levels.' /* AUTO-GENERATED */,
    },
    {
      path: '/tags',
      label: t('Tags') /* AUTO-GENERATED */,
      hasBottomSpacing: true,
    },
  ];

  return navItems;
}

export function useHidePickerConditions(): ((matches: { pathname: string; id: string }[]) => boolean)[] {
  return [];
}

export { useSettingsNavItems };
